import { ArrowBack } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Stack } from "@mui/material";
import { createContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from '../../assets/logos/icare_acrologo.png';
import NewUserActions from "../../components/Lifestyle/NewUserActions";
import ResultsStage1 from "../../components/Lifestyle/ResultsStage1";
import ResultsStage2 from "../../components/Lifestyle/ResultsStage2";
import ViewInputs from "../../components/Lifestyle/ViewInputs";
import AppHeader from "../../components/nav/AppHeader";
import BottomNav from "../../components/nav/BottomNav";
import Footer1 from "../../components/nav/Footer1";
import { sendReactGAPageView } from "../../consts/helpers";
import useAuthUser from "../../hooks/useAuthUser";
import Step1InputPage from "./Step1InputPage";
import Step2InputPage from "./Step2InputPage";

export const FootprintContext = createContext();
export default function EditLifestylePage() {
	const { pathname } = useLocation();

    var { userid, isNewUser, setNewUser }  = useAuthUser();
    const navigate = useNavigate();
    const [currentUserState, setCurrentUserState] = useState();

    const [stage, setStage] = useState(1); // 1,2,3,4,5 == input1, result1, input2, result2, actions
    const [inputs, setInputs] = useState({}); 
    
    // Stage 1 inputs
    const [expenditureInput, setExp] = useState('');
    // Stage 2 inputs
    const [dietPrefInput, setDietPref] = useState(null);
    const [regularCarUse, setRegCarUse] = useState(null);
    const [eatOutExpInput, setEatOut] = useState('');
    const [eatInExpInput, setEatIn] = useState('');
    const [transportExpInput, setTransport] = useState('');
    const [recreationExpInput, setRecreation] = useState('');
    const [utilitiesExpInput, setUtils] = useState('');

    // Flags
    const [isValidInputs1, setValid1] = useState(false); // Flag to set when all stage 1 inputs are filled
    const [isValidInputs2, setValid2] = useState(false); // Flag to set when all stage 2 inputs are filled
    const [isFetchLoading, setFetchLoading] = useState(true);

    const handleForwardButtonClick = () => {
        if (stage===5) navigate('/');
        else setStage(stage+1);
    }
    const handleBackButtonClick = () => {
        if (stage===1) navigate('/');
        else setStage(stage-1);
    }

    const callFetchAPI = () => {
        if (userid) {
            setFetchLoading(true);
            const url = process.env.REACT_APP_CALC_LAMBDA + '?' + new URLSearchParams({ uid: userid });
            fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
            }).then(
                function (response) {
                    return response.json();
                }
            ).then(
                function(data) {
                    // console.log(data)
                    const fetchedData = data?.result;
                    if (fetchedData) {
                        setExp(String(fetchedData?.expenditureInput || ''));
                        setDietPref(String(fetchedData?.dietPrefInput || ''));
                        setRegCarUse(fetchedData?.regularCarUse!==null ? Boolean(fetchedData?.regularCarUse) : null);
                        setEatOut(String(fetchedData?.eatOutExpInput || ''));
                        setEatIn(String(fetchedData?.eatInExpInput || ''));
                        setTransport(String(fetchedData?.transportExpInput || ''));
                        setRecreation(String(fetchedData?.recreationExpInput || ''));
                        setUtils(String(fetchedData?.utilitiesExpInput || ''));
                    } else setNewUser(false);
                    setFetchLoading(false);
                }
            ).catch(
                function(error) {
                    console.log("PROROUTE:", error)
                }
            );
        } else setNewUser(false);
    }

    const callCalcAPI = () => {
        const url = process.env.REACT_APP_CALC_LAMBDA;
        const body = {
            formdata: inputs,
            uid: userid,
        };
        // console.log(body)
        return fetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }).then(response => {
            return response.json();
        }).then(data => {
            // console.log(data);
            if (data?.isSuccessful !== '') {
                setNewUser(false)
                return data.result;
            }
        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error);
            return {};
        });
    }
    
    useEffect(()=>{
        setInputs(i => ({
            ...i,
            expenditureInput:expenditureInput,
            dietPrefInput:dietPrefInput,
            regularCarUse:regularCarUse,
            eatOutExpInput:eatOutExpInput,
            eatInExpInput:eatInExpInput,
            transportExpInput:transportExpInput,
            recreationExpInput:recreationExpInput,
            utilitiesExpInput:utilitiesExpInput,
        }));
        let set1IsValid = (expenditureInput!=='');
        setValid1(set1IsValid);
        let set2IsValid = (dietPrefInput!=='') && (regularCarUse!==null) && (eatOutExpInput!=='') && (eatInExpInput!=='') && (transportExpInput!=='') && (recreationExpInput!=='') && (utilitiesExpInput!=='');
        setValid2(set2IsValid);
    },[ expenditureInput,
        dietPrefInput,regularCarUse,eatOutExpInput,eatInExpInput,transportExpInput,recreationExpInput,utilitiesExpInput
    ])

    useEffect(() => {
		window.scrollTo(0, 0);
        if (currentUserState) { // is new 
            navigate(`/profile/edit/${stage}`);
        }
	}, [currentUserState, stage]);

    useEffect(() => {
        if (stage===1) setCurrentUserState(isNewUser); 
        if (!isNewUser) callFetchAPI();
        else setFetchLoading(false);
    }, [isNewUser, userid])

	useEffect(() => {
        var title = 'Your Carbon Footprint Profile';
        var setDocTitle = true;
        if (currentUserState) { // is new 
            title += `- Stage ${stage}`;
            setDocTitle = false;
        } else {
            navigate(`/profile/edit`);
        }
        sendReactGAPageView(pathname, title, setDocTitle);
    }, [pathname, currentUserState, stage])

    return (
        <>
        {
            currentUserState ?
            <Stack direction={'row'} spacing={1} sx={{ pt: '32px' }}>
                <IconButton onClick={handleBackButtonClick}>
                    <ArrowBack />
                </IconButton>
                <Box>
                    <img src={logo} alt='icare app logo' style={{ maxWidth: 100 }}/>
                </Box>
            </Stack>
            :
            <AppHeader />
        }

            <Box sx={{ flex: 'auto', py: 2 }} className='spread-y'>
            <FootprintContext.Provider value={{
                inputs: inputs,
                expenditureInput:[expenditureInput, setExp],
                dietPrefInput:[dietPrefInput, setDietPref],
                eatOutExpInput:[eatOutExpInput, setEatOut],
                eatInExpInplut:[eatInExpInput, setEatIn],
                transportExpInput:[transportExpInput, setTransport],
                utilitiesExpInput:[utilitiesExpInput, setUtils],
                recreationExpInput:[recreationExpInput, setRecreation],
                regularCarUse:[regularCarUse, setRegCarUse],
            }} >
                {
                    currentUserState ? (
                        stage===1 ? 
                            <Step1InputPage isValidInputs={isValidInputs1} handleProceed={handleForwardButtonClick} />
                        : stage===2 ? 
                            <ResultsStage1 callCalcAPI={callCalcAPI} handleProceed={handleForwardButtonClick} />
                        : stage===3 ? 
                            <Step2InputPage isValidInputs={isValidInputs2} handleProceed={handleForwardButtonClick} />
                        : stage===4 ? 
                            <ResultsStage2 callCalcAPI={callCalcAPI} handleProceed={handleForwardButtonClick} />
                        : stage===5 ? 
                            <NewUserActions />
                        : <>Something went wrong. Please refresh this page.</>
                    ) : (
                    isFetchLoading ? 
                        <Box className='centered-container' sx={{ flex: 'auto' }}>
                            <CircularProgress color='secondary' />
                        </Box>
                    :
                        <ViewInputs isValidInputs={(isValidInputs1 && isValidInputs2)} callCalcAPI={callCalcAPI} />
                    )
                }
                <Footer1 />
            </FootprintContext.Provider>
            </Box>

            { !currentUserState &&
                <BottomNav />
            }
        </>
    )
}